import classNames from "classnames";
import React, { useMemo } from "react";
import { useForm } from "react-hook-form";
import { Badge, Card, Col, Form, Label, Row } from "reactstrap";
import Swal from "sweetalert2";
import {
  BlockDes,
  BlockHeadContent,
  BlockTitle,
  Button,
  Icon,
  ReactDataTable,
} from "../../../components/Component";
import Content from "../../../layout/content/Content";
import Head from "../../../layout/head/Head";
import { useDataFetch } from "../../../hooks/useDataFetch";
import { toast } from "react-toastify";

/**
 * Catálogo de tipos de árbol (Voluntario, Donante, Homenaje...).
 *
 * Mismo diseño que los demás maestros. La diferencia es que aquí los errores
 * del API sí se muestran: un nombre repetido o un tipo en uso no se pueden
 * dar por buenos, porque el tipo de cada árbol depende de este listado.
 */
const TreeType = () => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  const formClass = classNames({
    "form-validate": true,
  });

  const { getData, sendJson, invalidateQuery } = useDataFetch();

  const { data, isLoading, refetch } = getData(
    ["tree-types"],
    "/panel/tree-types",
    { enabled: true }
  );

  const refreshTable = async () => {
    await refetch();
    invalidateQuery(["tree-types"]);
  };

  const { mutate: createType, isLoading: isPostLoading } = sendJson(
    "POST",
    "/panel/tree-types",
    {
      onSuccess: (tipo) => {
        reset();
        toast.success(`Se creó el tipo «${tipo.name}».`);
        refreshTable();
      },
    }
  );

  const { mutate: deleteType } = sendJson("DELETE", "/panel/tree-types", {
    onSuccess: () => {
      toast.success("Se eliminó el tipo.");
      refreshTable();
    },
  });

  const onFormSubmit = (e) => {
    createType({ name: e.name });
  };

  const handleDelete = (tipo) => {
    Swal.fire({
      title: "Confirmación",
      text: `¿Desea eliminar el tipo «${tipo.name}»?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Si",
    }).then((result) => {
      if (result.isConfirmed) deleteType({ id: tipo.id });
    });
  };

  const dataTableColumns = useMemo(
    () => [
      {
        name: "Código",
        selector: (row) => row.id,
        cell: (row) => (
          <div className="user-card mt-2 mb-2">
            <span>{row.id}</span>
          </div>
        ),
        sortable: true,
      },
      {
        name: "Nombre",
        selector: (row) => row.name,
        minWidth: "160px",
        cell: (row) => (
          <span className="tb-amount">
            {row.name}
            {row.is_default && (
              <Badge color="primary" className="ms-2 badge-dim">
                Por defecto
              </Badge>
            )}
          </span>
        ),
        sortable: true,
      },
      {
        name: "Árboles",
        selector: (row) => row.trees_count,
        cell: (row) => <span>{row.trees_count}</span>,
        sortable: true,
      },
      {
        name: "Acciones",
        cell: (row) => {
          // El API también lo impide; aquí se explica antes de intentarlo.
          const motivo = row.is_default
            ? "Es el tipo por defecto"
            : row.trees_count > 0
            ? `Lo usan ${row.trees_count} árbol(es)`
            : null;

          return (
            <span title={motivo ?? "Eliminar"}>
              <Button
                disabled={motivo !== null}
                onClick={() => handleDelete(row)}
              >
                <Icon name="trash" size={20} />
              </Button>
            </span>
          );
        },
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  return (
    <React.Fragment>
      <Head title="Tipos de árbol"></Head>
      <Content>
        <Card className="">
          <div className="card-aside-wrap">
            <div
              className={`card-aside card-aside-left user-aside toggle-slide toggle-slide-left toggle-break-lg p-4`}
            >
              <Form className={formClass} onSubmit={handleSubmit(onFormSubmit)}>
                <Row className="g-gs">
                  <Col md="12">
                    <BlockHeadContent>
                      <BlockTitle tag="h4">Crear tipos de árbol</BlockTitle>
                      <BlockDes>
                        <p className="mt-1">
                          Estos son los valores que acepta la columna{" "}
                          <strong>tipo</strong> del Excel de nuevos árboles y
                          el formulario de crear árbol. Si el tipo viene vacío
                          se usa <strong>Voluntario</strong>.
                        </p>
                      </BlockDes>
                    </BlockHeadContent>
                    <div className="form-group mt-4">
                      <Label className="form-label" htmlFor="tipo-nombre">
                        Nombre
                      </Label>
                      <div className="form-control-wrap">
                        <input
                          type="text"
                          id="tipo-nombre"
                          maxLength={100}
                          {...register("name", {
                            required: true,
                            validate: (valor) => valor.trim() !== "",
                          })}
                          className="form-control"
                        />
                        {errors.name && (
                          <span className="invalid">
                            Este campo es requerido
                          </span>
                        )}
                      </div>
                    </div>
                  </Col>

                  <Col md="12">
                    <div className="form-group">
                      <Button
                        color="primary"
                        size="lg"
                        disabled={isPostLoading}
                      >
                        {isPostLoading ? "Guardando..." : "Guardar"}
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>
            </div>
            <div className="card-inner card-inner-lg">
              {Array.isArray(data) && !isLoading && (
                <ReactDataTable
                  data={data}
                  columns={dataTableColumns}
                  pagination
                  className="nk-tb-list"
                />
              )}
            </div>
          </div>
        </Card>
      </Content>
    </React.Fragment>
  );
};

export default TreeType;
